import { InAppState } from "../base/InAppState";
import { Organisation } from "../../models/Organisation";
import template from './SettingsTemplate.html';

export class SettingsState extends InAppState
{
    static ROUTE = '/settings';
    static ID = 'admin-settings';

    async enter()
    {
        this.app.view.blockUI( this.app.view.pageContent, 'Loading settings...' );

        this.organisation = null;
        const organisationId = this.app.user.organisationId;

        const { result } = await this.app.api.get( `/organisation/${organisationId}` );
        if ( result.status === 200 && result.data )
        {
            this.organisation = new Organisation( result.data );
        }

        this.dpa = await this._loadDpaStatus();

        this._render();
        this.app.view.unblockUI( this.app.view.pageContent );
    }

    _render( activeTabSelector = null )
    {
        this.app.view.render(
            this.app.view.pageContent,
            template,
            { organisation: this.organisation, dpa: this.dpa }
        );

        this._bindEventListeners();

        if ( activeTabSelector )
        {
            this.switchTab( activeTabSelector );
        }
    }

    _getActiveTabSelector()
    {
        const activeTab = this.app.view.pageContent.querySelector( '[data-type="settings-tab"].active' );
        return activeTab?.getAttribute( 'data-tab-target' ) || null;
    }

    async _loadDpaStatus()
    {
        try
        {
            const { result } = await this.app.api.get( '/organisation/dpa' );
            return +result.status === 200 ? result.data : null;
        }
        catch
        {
            return null;
        }
    }

    async _acceptDpa( button )
    {
        button.disabled = true;
        try
        {
            const { result } = await this.app.api.post( '/organisation/dpa', { version: this.dpa.currentVersion } );
            if ( +result.status !== 200 || result.data?.isAccepted !== true ) throw new Error( result.data?.message || 'Accept failed' );
            this.dpa = result.data;
            this._render( this._getActiveTabSelector() );
            this.app.view.toast( 'Data Processing Agreement accepted.', 'success' );
        }
        catch
        {
            button.disabled = false;
            this.app.view.toast( 'Could not accept the Data Processing Agreement. Please reload and try again.', 'error' );
        }
    }

    _bindEventListeners()
    {
        const acceptDpa = this.app.view.pageContent.querySelector( '[data-type="accept-dpa"]' );
        if ( acceptDpa ) this.addEventListener( acceptDpa, 'click', () => this._acceptDpa( acceptDpa ) );

        const withdraw = this.app.view.pageContent.querySelector( '[data-type="withdraw-acquisition"]' );
        if ( withdraw ) this.addEventListener( withdraw, 'click', async () => {
            withdraw.disabled = true;
            try
            {
                const { result } = await this.app.api.delete( '/organisation/acquisition' );
                if ( +result.status !== 200 || result.data?.measurementEnabled !== false ) throw new Error( 'Withdrawal failed' );
                delete this.organisation.data.acquisition;
                this._render( this._getActiveTabSelector() );
                this.app.view.toast( 'Previous campaign data has been removed.', 'success' );
            }
            catch
            {
                withdraw.disabled = false;
                this.app.view.toast( 'Could not stop measurement. Please try again.', 'error' );
            }
        } );
        this.app.view.pageContent.querySelectorAll( '[data-type="settings-tab"]' ).forEach( ( tab ) => {
            this.addEventListener( tab, 'click', () => {
                this.switchTab( tab.getAttribute( 'data-tab-target' ) );
            });
        });

        // General Settings Modal
        const editGeneralBtn = this.app.view.pageContent.querySelector( '[data-type="edit-general-settings"]' );
        if ( editGeneralBtn )
        {
            this.addEventListener( editGeneralBtn, 'click', () => this._showModal( 'editGeneralSettingsModal' ) );
        }

        this._bindModalButtons( 'general', 'editGeneralSettingsModal', () => this._saveGeneralSettings() );

        // Organisation Modal
        const editOrgBtn = this.app.view.pageContent.querySelector( '[data-type="edit-organisation"]' );
        if ( editOrgBtn )
        {
            this.addEventListener( editOrgBtn, 'click', () => this._showModal( 'editOrganisationModal' ) );
        }

        this._bindModalButtons( 'organisation', 'editOrganisationModal', () => this._saveOrganisation() );

    }

    _bindModalButtons( prefix, modalId, saveCallback )
    {
        const closeBtn = this.app.view.pageContent.querySelector( `[data-type="${prefix}-modal-close"]` );
        const cancelBtn = this.app.view.pageContent.querySelector( `[data-type="${prefix}-modal-cancel"]` );
        const saveBtn = this.app.view.pageContent.querySelector( `[data-type="${prefix}-modal-save"]` );

        if ( closeBtn )
        {
            this.addEventListener( closeBtn, 'click', () => this._hideModal( modalId ) );
        }

        if ( cancelBtn )
        {
            this.addEventListener( cancelBtn, 'click', () => this._hideModal( modalId ) );
        }

        if ( saveBtn )
        {
            this.addEventListener( saveBtn, 'click', saveCallback );
        }
    }

    _showModal( modalId )
    {
        const modal = this.app.view.pageContent.querySelector( `#${modalId}` );
        if ( modal )
        {
            modal.classList.add( 'show' );
            modal.style.display = 'block';
            document.body.classList.add( 'modal-open' );

            const backdrop = document.createElement( 'div' );
            backdrop.className = 'modal-backdrop fade show';
            backdrop.id = `${modalId}Backdrop`;
            document.body.appendChild( backdrop );
        }
    }

    _hideModal( modalId )
    {
        const modal = this.app.view.pageContent.querySelector( `#${modalId}` );
        const backdrop = document.getElementById( `${modalId}Backdrop` );

        if ( modal )
        {
            modal.classList.remove( 'show' );
            modal.style.display = 'none';
            document.body.classList.remove( 'modal-open' );
        }

        if ( backdrop )
        {
            backdrop.remove();
        }
    }

    async _saveGeneralSettings()
    {
        const form = this.app.view.pageContent.querySelector( '#edit-general-settings-form' );
        const retentionInput = form?.querySelector( '#edit-document-retention' );
        const currencyInput = form?.querySelector( '#edit-default-currency' );
        const timezoneInput = form?.querySelector( '#edit-default-timezone' );

        const modal = this.app.view.pageContent.querySelector( '#editGeneralSettingsModal' );
        this.app.view.blockUI( modal.querySelector( '.modal-content' ), 'Saving settings...' );

        const settings = {
            documentRetentionDays: parseInt( retentionInput?.value ) || 30,
            defaultCurrency: currencyInput?.value || 'USD',
            defaultTimezone: timezoneInput?.value || 'UTC'
        };

        try
        {
            const { result } = await this.app.api.patch( `/organisation/${this.organisation.id}`, { settings } );

            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );

            if ( result.status === 200 && result.data )
            {
                const activeTab = this._getActiveTabSelector();
                this.organisation = new Organisation( result.data );
                this._hideModal( 'editGeneralSettingsModal' );
                this._render( activeTab );
                this.app.view.toast( 'Settings updated successfully', 'success' );
            }
            else
            {
                this.app.view.toast( result.data?.message || 'Failed to update settings', 'error' );
            }
        }
        catch ( error )
        {
            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );
            this.app.view.toast( 'An error occurred while saving settings', 'error' );
        }
    }

    async _saveOrganisation()
    {
        const form = this.app.view.pageContent.querySelector( '#edit-organisation-form' );
        const nameInput = form?.querySelector( '#edit-organisation-name' );
        const streetInput = form?.querySelector( '#edit-organisation-street' );
        const cityInput = form?.querySelector( '#edit-organisation-city' );
        const stateInput = form?.querySelector( '#edit-organisation-state' );
        const postalInput = form?.querySelector( '#edit-organisation-postal' );
        const countryInput = form?.querySelector( '#edit-organisation-country' );

        if ( !nameInput?.value?.trim() )
        {
            this.app.view.toast( 'Organisation name is required', 'error' );
            return;
        }

        const modal = this.app.view.pageContent.querySelector( '#editOrganisationModal' );
        this.app.view.blockUI( modal.querySelector( '.modal-content' ), 'Saving organisation...' );

        const payload = {
            name: nameInput.value.trim(),
            data: {
                address: {
                    street: streetInput?.value?.trim() || null,
                    city: cityInput?.value?.trim() || null,
                    state: stateInput?.value?.trim() || null,
                    postalCode: postalInput?.value?.trim() || null,
                    country: countryInput?.value?.trim() || null
                }
            }
        };

        try
        {
            const { result } = await this.app.api.patch( `/organisation/${this.organisation.id}`, payload );

            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );

            if ( result.status === 200 && result.data )
            {
                const activeTab = this._getActiveTabSelector();
                this.organisation = new Organisation( result.data );
                this._hideModal( 'editOrganisationModal' );
                this._render( activeTab );
                this.app.view.toast( 'Organisation updated successfully', 'success' );
            }
            else
            {
                this.app.view.toast( result.data?.message || 'Failed to update organisation', 'error' );
            }
        }
        catch ( error )
        {
            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );
            this.app.view.toast( 'An error occurred while saving organisation', 'error' );
        }
    }

    switchTab( targetSelector )
    {
        const container = this.app.view.pageContent;

        container.querySelectorAll( '[data-type="settings-tab"]' ).forEach( ( tab ) => {
            tab.classList.remove( 'active' );
            tab.setAttribute( 'aria-selected', 'false' );
        });

        container.querySelectorAll( '.tab-pane' ).forEach( ( pane ) => {
            pane.classList.remove( 'show', 'active' );
        });

        const activeTab = container.querySelector( `[data-type="settings-tab"][data-tab-target="${targetSelector}"]` );
        if ( activeTab )
        {
            activeTab.classList.add( 'active' );
            activeTab.setAttribute( 'aria-selected', 'true' );
        }

        const activePane = container.querySelector( targetSelector );
        if ( activePane )
        {
            activePane.classList.add( 'show', 'active' );
        }

    }

    async exit()
    {
    }

    getContentHeaderData()
    {
        return {
            title: 'Settings',
            breadcrumb: [ 'Admin', 'Settings' ]
        };
    }
}
